import { useEffect, useRef } from 'react'
import { User } from 'lucide-react'

export interface TranscriptSegment {
  text: string
  speaker?: number | null
  is_final: boolean
  timestamp: number
}

interface LiveTranscriptProps {
  segments: TranscriptSegment[]
  interimText?: string
}

export function LiveTranscript({ segments, interimText }: LiveTranscriptProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [segments, interimText])

  if (segments.length === 0 && !interimText) {
    return (
      <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400 dark:text-gray-500 text-sm">
          <Mic className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Waiting for audio...</p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6 h-64 overflow-y-auto"
    >
      <div className="space-y-4">
        {segments.map((segment, i) => (
          <div key={i} className="flex gap-3">
            {segment.speaker !== null && segment.speaker !== undefined && (
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-600/20 text-brand-400 flex items-center justify-center text-xs">
                {segment.speaker}
              </div>
            )}
            <div className="flex-1">
              <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{segment.text}</p>
            </div>
          </div>
        ))}
        
        {interimText && (
          <div className="flex gap-3 opacity-60">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <User size={12} className="text-gray-500 dark:text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="text-gray-500 dark:text-gray-400 italic leading-relaxed">{interimText}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Mic({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  )
}
